import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ShieldCheck,
  ReceiptText,
  Gauge,
  FileWarning,
  CalendarClock,
  Target,
  Check,
  Megaphone,
  Building2,
  Scale,
  PlayCircle,
  X,
  ChevronDown,
  Lock,
  FolderCheck,
  Landmark,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { ClientOutcomes } from "@/components/ClientOutcomes";
import { api } from "@/lib/api";
import { ConsultationForm } from "@/pages/dashboard/ConsultationForm";

const TIER_FORM_PLAN = {
  "RetainProof Core": "Core Accountability",
  "RetainProof Performance": "Performance Intelligence",
  "RetainProof Executive Oversight": "Executive Oversight",
};

function demoSessionId() {
  let s = localStorage.getItem("rp_sid");
  if (!s) {
    s = Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("rp_sid", s);
  }
  return s;
}
const trackDemo = (event, meta) =>
  api.post("/public/analytics/event", { event, session_id: demoSessionId(), meta }).catch(() => {});

const HERO_VARIANTS = {
  A: { pre: "Measure marketing performance by ", em: "retained matters", post: "—not agency-reported activity." },
  B: { pre: "Know exactly which ", em: "marketing dollars", post: " become signed cases." },
};
function getHeroVariant() {
  let v = localStorage.getItem("rp_ab_hero");
  if (v !== "A" && v !== "B") {
    v = Math.random() < 0.5 ? "A" : "B";
    localStorage.setItem("rp_ab_hero", v);
  }
  return v;
}

function RoiCalculator() {
  const [spend, setSpend] = useState(40000);
  const [cases, setCases] = useState(12);
  const [waste, setWaste] = useState(20);
  const costPerCase = cases > 0 ? spend / cases : 0;
  const monthlySavings = spend * (waste / 100);
  const annualSavings = monthlySavings * 12;
  const fmt = (n) => "$" + Math.round(n).toLocaleString();
  const fields = [
    { label: "Monthly marketing spend", val: spend, set: setSpend, min: 1000, max: 300000, step: 1000, disp: fmt(spend), tid: "roi-spend" },
    { label: "Signed cases / month", val: cases, set: setCases, min: 1, max: 100, step: 1, disp: cases, tid: "roi-cases" },
    { label: "Est. unverified / wasted spend", val: waste, set: setWaste, min: 0, max: 60, step: 1, disp: `${waste}%`, tid: "roi-waste" },
  ];
  return (
    <div className="border border-white/10 bg-[#12324D] p-6" data-testid="hero-roi-calculator">
      <p className="font-mono-data text-[10px] uppercase tracking-widest text-[#D5A44A]">What could better marketing accountability be worth?</p>
      <div className="mt-4 space-y-4">
        {fields.map((f) => (
          <div key={f.label}>
            <div className="flex justify-between text-xs text-zinc-400 mb-1">
              <span>{f.label}</span>
              <span className="font-mono-data text-white">{f.disp}</span>
            </div>
            <input
              type="range" min={f.min} max={f.max} step={f.step} value={f.val}
              onChange={(e) => f.set(Number(e.target.value))}
              data-testid={f.tid}
              className="w-full accent-[#D5A44A] cursor-pointer"
            />
          </div>
        ))}
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="border border-white/10 bg-black/30 p-3">
          <p className="font-mono-data text-[9px] uppercase tracking-widest text-zinc-500">Illustrative cost / retained case</p>
          <p className="font-display text-2xl text-white mt-1" data-testid="roi-cost-per-case">{fmt(costPerCase)}</p>
        </div>
        <div className="border border-[#D5A44A]/30 bg-[#D5A44A]/5 p-3">
          <p className="font-mono-data text-[9px] uppercase tracking-widest text-[#D5A44A]">Potential monthly waste exposure</p>
          <p className="font-display text-2xl text-[#D5A44A] mt-1" data-testid="roi-monthly-savings">{fmt(monthlySavings)}</p>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="border border-white/10 bg-black/30 p-3">
          <p className="font-mono-data text-[9px] uppercase tracking-widest text-zinc-500">Potential annual waste exposure</p>
          <p className="font-display text-2xl text-white mt-1" data-testid="roi-annual-exposure">{fmt(annualSavings)}</p>
        </div>
        <div className="border border-white/10 bg-black/30 p-3">
          <p className="font-mono-data text-[9px] uppercase tracking-widest text-zinc-500">Annual marketing spend analyzed</p>
          <p className="font-display text-2xl text-white mt-1" data-testid="roi-annual-spend">{fmt(spend * 12)}</p>
        </div>
      </div>
      <p className="mt-3 text-[11px] text-zinc-500 leading-relaxed" data-testid="roi-disclaimer">
        Illustrative estimate only. Actual RetainProof findings depend on your firm's records, vendor agreements, invoices, lead data and retained matters. Calculator results are not evidence of actual savings or recoveries.
      </p>
    </div>
  );
}

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};

const features = [
  {
    icon: ReceiptText,
    title: "Invoice Reconciliation",
    body: "Forward vendor invoices. AI compares them against contracts and platform spend, flagging duplicate charges, unapproved increases and fees that exceed the agreement.",
    span: "md:col-span-2 md:row-span-2",
    image: "/product-invoice-recon.png",
  },
  {
    icon: Target,
    title: "Cost Per Retained Case",
    body: "Not cost per lead. The metric that actually maps marketing dollars to signed cases — by channel, campaign, vendor, practice area and office.",
    span: "md:col-span-1",
  },
  {
    icon: CalendarClock,
    title: "Renewal Alerts",
    body: "Never get locked into an underperforming vendor again. Alerts 30/60/90 days before auto-renewal and cancellation deadlines.",
    span: "md:col-span-1",
  },
  {
    icon: Gauge,
    title: "Vendor Scorecards",
    body: "Compare your Google Ads agency vs. TV buyer vs. billboard company vs. directory on spend, signed cases, ROI and responsiveness.",
    span: "md:col-span-1",
  },
  {
    icon: FileWarning,
    title: "Intake vs. Marketing Failure",
    body: "Separate poor leads from good leads your team dropped. See which qualified callers were never contacted within 15 minutes.",
    span: "md:col-span-1",
  },
];

export default function Landing() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [variant] = useState(getHeroVariant);
  const [consult, setConsult] = useState({ open: false, type: "general_consultation", plan: "", title: "" });
  const openConsult = (type, plan, title) => setConsult({ open: true, type, plan, title });
  const demoTracked = useRef({});
  const { enterDemo } = useAuth();
  const navigate = useNavigate();
  const [demoFirmOpen, setDemoFirmOpen] = useState(false);
  const [demoEmail, setDemoEmail] = useState("");
  const [demoBusy, setDemoBusy] = useState(false);
  const [banner, setBanner] = useState(null);

  const submitDemoFirm = async (e) => {
    e.preventDefault();
    if (!demoEmail) return;
    setDemoBusy(true);
    try {
      await enterDemo(demoEmail);
      navigate("/app");
    } catch (err) {
      setDemoBusy(false);
    }
  };

  const openDemo = () => {
    demoTracked.current = {};
    trackDemo("demo_open");
    setDemoOpen(true);
  };

  useEffect(() => {
    if (!sessionStorage.getItem("rp_ab_hero_imp")) {
      sessionStorage.setItem("rp_ab_hero_imp", "1");
      trackDemo("ab_impression", { experiment: "hero_headline", variant });
    }
  }, [variant]);

  useEffect(() => {
    api.get("/public/baseline/settings").then((r) => { if (r.data?.banner_enabled) setBanner(r.data); }).catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setDemoOpen(false); };
    if (demoOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [demoOpen]);

  return (
    <div className="dark relative min-h-screen bg-[#0A1B2E] text-[#F5F5F5] font-body overflow-hidden">
      <Seo title="RetainProof — Know which marketing dollars become signed cases" description="See cost per retained case, catch agency overbilling, and hold every marketing vendor accountable — independent marketing performance analytics for PI and plaintiff law firms." path="/" />
      <div className="grain-overlay" />

      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="flex items-center gap-2.5 font-body font-bold text-2xl tracking-tight" data-testid="brand-logo">
            <img src="/retainproof-icon-dark.png" alt="RetainProof" className="h-8 w-auto" />
            <span>Retain<span className="text-[#0E7C7B]">Proof</span><sup className="text-[0.5em] font-normal align-super">™</sup></span>
          </span>
          <div className="flex items-center gap-1 sm:gap-3">
            <Link to="/features" data-testid="nav-features-link">
              <Button variant="ghost" className="hidden sm:inline-flex text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">
                Features
              </Button>
            </Link>
            <Link to="/about" data-testid="nav-about-link">
              <Button variant="ghost" className="hidden sm:inline-flex text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">
                About
              </Button>
            </Link>
            <Link to="/pricing" data-testid="nav-pricing-link">
              <Button variant="ghost" className="hidden sm:inline-flex text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">
                Pricing
              </Button>
            </Link>
            <Link to="/vs" data-testid="nav-vs-link">
              <Button variant="ghost" className="hidden md:inline-flex text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">
                Why independent
              </Button>
            </Link>
            <Link to="/insights" data-testid="nav-insights-link">
              <Button variant="ghost" className="hidden lg:inline-flex text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">
                Insights
              </Button>
            </Link>
            <a href="/#assessments" data-testid="nav-assessments-link">
              <Button variant="ghost" className="hidden sm:inline-flex text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">
                Assessments
              </Button>
            </a>
            <Link to="/login" data-testid="nav-login-link">
              <Button variant="ghost" className="text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">
                Sign in
              </Button>
            </Link>
            <Link to="/register" data-testid="nav-register-link">
              <Button className="bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none font-medium">
                Get started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Promotional banner (admin-toggleable) */}
      {banner && (
        <div className="relative z-40 mt-16 border-b border-[#D5A44A]/30 bg-[#12324D]" data-testid="baseline-promo-banner">
          <div className="max-w-7xl mx-auto px-6 py-3.5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6">
            <div className="flex-1 min-w-0">
              <p className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-[#D5A44A]">
                Introductory RetainProof Baseline Review — ${(banner.intro_price ?? 750).toLocaleString()}
              </p>
              <p className="text-sm text-zinc-300 mt-1 leading-snug">
                Get an independent view of how your firm's marketing spend and vendor performance connect to qualified conversations and retained matters. {banner.availability_text || "Available for a limited time to a limited number of new law-firm clients."}
              </p>
            </div>
            <Link to="/baseline-review" data-testid="baseline-banner-cta" onClick={() => api.post("/public/baseline/track", { event: "banner_click", session_id: "landing" }).catch(() => {})} className="shrink-0 inline-flex items-center bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none px-5 h-10 text-sm font-medium transition-colors">
              Reserve Your Review
            </Link>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-40 pb-28 grid lg:grid-cols-2 gap-16 items-start">
        <div>
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={0}>
            <p className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#D5A44A] mb-6">
              Independent marketing accountability for personal-injury &amp; plaintiff law firms
            </p>
          </motion.div>
          <motion.h1
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={1}
            className="font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
          >
            {HERO_VARIANTS[variant].pre}
            <span className="italic text-[#D5A44A]">{HERO_VARIANTS[variant].em}</span>
            {HERO_VARIANTS[variant].post}
          </motion.h1>
          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={2}
            className="mt-8 text-lg text-zinc-400 max-w-xl leading-relaxed"
          >
            Built for managing partners at spend-heavy PI and plaintiff firms. Stop relying solely
            on agency-reported analytics — compare what your marketing agencies and lead generators
            promise with what your firm actually spends, and the cases it ultimately retains.
          </motion.p>
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={3}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Button onClick={() => document.getElementById("assessments")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-cta-primary" className="bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-12 px-8 text-base font-medium group">
              See Assessment Options
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button onClick={() => openConsult("general_consultation", "", "Schedule a RetainProof Consultation")} data-testid="hero-cta-register" variant="outline" className="rounded-none h-12 px-8 text-base border border-white/25 bg-transparent text-white hover:bg-white/5">
              Request a consultation
            </Button>
          </motion.div>
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={4}
            className="mt-5 text-sm"
          >
            <a href="/roi-showcase" target="_blank" rel="noopener noreferrer" data-testid="hero-watch-how-it-works" className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors group">
              See how it works <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9 }}
          className="relative space-y-6"
        >
          <div className="relative" data-testid="hero-before-after">
            <div className="absolute -inset-4 bg-[#D5A44A]/10 blur-3xl rounded-full" />
            <div className="relative border border-white/10 bg-[#0B1F33]">
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">Before RetainProof</p>
                  <span className="font-mono-data text-[9px] uppercase tracking-widest text-zinc-600 border border-white/10 px-2 py-0.5">Illustrative</span>
                </div>
                <p className="font-display text-2xl text-white/70 mt-2">$84,000 marketing spend</p>
                <ul className="mt-4 space-y-1.5 text-sm text-zinc-500">
                  {["Multiple agency / vendor reports", "Lead quality unclear", "True vendor ROI unknown", "Retained-case attribution incomplete", "Billing accuracy difficult to verify"].map((t) => (
                    <li key={t} className="flex gap-2"><X className="h-3.5 w-3.5 text-zinc-600 shrink-0 mt-0.5" />{t}</li>
                  ))}
                </ul>
              </div>
              <div className="flex items-center justify-center py-2.5 bg-[#D5A44A]/10 border-b border-white/10">
                <span className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-[#D5A44A] inline-flex items-center gap-2">After RetainProof <ArrowRight className="h-3.5 w-3.5" /></span>
              </div>
              <div className="p-6">
                <ul className="space-y-1.5 text-sm text-zinc-200">
                  {["Marketing spend independently reconciled", "Vendors compared against actual firm outcomes", "Cost per retained case calculated", "Questioned or unsupported charges identified", "Underperforming channels highlighted", "Clear recommendations: challenge, reduce, preserve or expand"].map((t) => (
                    <li key={t} className="flex gap-2"><Check className="h-3.5 w-3.5 text-[#156F52] shrink-0 mt-0.5" />{t}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <RoiCalculator />
        </motion.div>
      </section>

      {/* Trust badge strip */}
      <section className="relative z-10 border-b border-white/10 bg-[#0B1F33]" data-testid="landing-trust-strip">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-8">
            {[
              [Target, "Independent Reporting"],
              [ShieldCheck, "Vendor-Agnostic Analysis"],
              [FolderCheck, "Retained Case Tracking"],
              [Search, "ROI Transparency"],
              [Landmark, "Plaintiff-Firm Focused"],
              [Lock, "Secure Data Platform"],
            ].map(([Icon, label]) => (
              <div key={label} className="flex flex-col items-center text-center gap-3" data-testid="trust-badge">
                <Icon className="h-7 w-7 text-[#0E7C7B]" strokeWidth={1.5} />
                <span className="font-mono-data text-[10px] sm:text-[11px] uppercase tracking-[0.15em] text-zinc-300 leading-snug">{label}</span>
                <span className="h-px w-6 bg-[#D5A44A]/50" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof in the numbers */}
      <section className="relative z-10 border-b border-white/10 bg-[#0B1F33]" data-testid="landing-proof-numbers">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <p className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#D5A44A] mb-8 text-center">Proof in the numbers</p>
          <div className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[
              ["$24K+", "Recovered in documented client outcomes"],
              ["$16K+", "Recovered after billing and deliverable discrepancies were identified"],
              ["$8K", "Recovered from improperly classified lead charges"],
            ].map(([stat, label]) => (
              <div key={label} className="bg-[#0B1F33] px-6 py-10 text-center flex flex-col items-center" data-testid="proof-stat">
                <span className="font-display text-5xl sm:text-6xl text-[#D5A44A] leading-none">{stat}</span>
                <p className="mt-4 text-sm text-zinc-400 leading-relaxed max-w-[16rem]">{label}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-zinc-500 text-center max-w-3xl mx-auto leading-relaxed" data-testid="proof-numbers-disclaimer">
            Client identities withheld for confidentiality. Outcomes are based on individual engagements and are not guarantees of future results.
          </p>
        </div>
      </section>

      {/* What your assessment can reveal */}
      <section className="relative z-10 border-b border-white/10 bg-[#0A1B2E]" data-testid="landing-reveal">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#0E7C7B] mb-4">Sample assessment · illustrative</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight max-w-3xl">What Your RetainProof Assessment Can Reveal</h2>
          <div className="mt-10 overflow-x-auto border border-white/10" data-testid="reveal-table">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="bg-white/5 text-left font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="p-4">Vendor</th><th className="p-4">Spend</th><th className="p-4">Retained cases</th><th className="p-4">Cost / retained case</th><th className="p-4">Finding</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {[
                  ["Agency A", "$24,500", "7", "$3,500", "Review Recommended", false],
                  ["Lead Provider B", "$18,200", "14", "$1,300", "Performing", true],
                  ["Agency C", "$11,750", "2", "$5,875", "Review Recommended", false],
                ].map((r) => (
                  <tr key={r[0]}>
                    <td className="p-4 text-white font-medium">{r[0]}</td>
                    <td className="p-4 text-zinc-300">{r[1]}</td>
                    <td className="p-4 text-zinc-300">{r[2]}</td>
                    <td className="p-4 text-zinc-300">{r[3]}</td>
                    <td className="p-4">
                      <span className={`inline-flex items-center font-mono-data text-[10px] uppercase tracking-widest px-2.5 py-1 border ${r[5] ? "text-[#3ea87f] border-[#156F52]/40 bg-[#156F52]/10" : "text-[#d98686] border-[#B54747]/40 bg-[#B54747]/10"}`}>{r[4]}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-6 text-sm text-zinc-400 max-w-3xl leading-relaxed">See which vendors are producing retained matters, which costs cannot be reconciled, and where your marketing dollars may need to be challenged or reallocated.</p>
          <p className="mt-3 font-mono-data text-[10px] uppercase tracking-widest text-zinc-600">All figures shown are illustrative sample data.</p>
        </div>
      </section>

      {/* Industry data — third-party recency signal */}
      <section className="relative z-10 border-b border-white/10 bg-[#0B1F33]" data-testid="landing-industry-data">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#0E7C7B] mb-4">Industry data · 2024–2025</p>
          <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight max-w-3xl">The numbers behind the accountability gap.</h2>
          <p className="mt-4 text-base text-zinc-400 max-w-2xl leading-relaxed">Independent research shows how much marketing spend and opportunity slip through unmeasured — the gap a RetainProof assessment is built to close.</p>
          <div className="mt-10 grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[
              { stat: "67%", body: "of law firms fail to respond to a prospective client's email.", src: "Clio 2024 Legal Trends Report", href: "https://www.clio.com/blog/highlights-from-2024-legal-trends-report/" },
              { stat: "48%", body: "of law firms are effectively unreachable by phone.", src: "Clio 2024 Legal Trends Report", href: "https://www.clio.com/blog/highlights-from-2024-legal-trends-report/" },
              { stat: "74%", body: "of law firms report wasting money on low-ROI marketing campaigns.", src: "LexGro, 2025", href: "https://lexgro.com/insights/law-firm-wasted-marketing-spend/" },
            ].map((s) => (
              <div key={s.body} className="bg-[#0B1F33] p-8 flex flex-col" data-testid="industry-stat">
                <span className="font-display text-5xl sm:text-6xl text-[#D5A44A] leading-none">{s.stat}</span>
                <p className="mt-4 text-sm text-zinc-300 leading-relaxed flex-1">{s.body}</p>
                <a href={s.href} target="_blank" rel="noopener noreferrer" className="mt-5 font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#D5A44A] transition-colors inline-flex items-center gap-1.5" data-testid="industry-stat-source">
                  Source: {s.src} <ArrowRight className="h-3 w-3" />
                </a>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-zinc-500 max-w-3xl leading-relaxed">Figures cited from third-party industry research and reflect the broader legal market, not RetainProof clients.</p>
        </div>
      </section>

      {/* Objection handling: no lock-in + independent + trust/proof */}
      <section className="relative z-10 border-b border-white/10 bg-[#0A1B2E]" data-testid="landing-objections">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="border border-white/10 bg-[#12324D] p-7" data-testid="objection-lockin">
            <p className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-[#D5A44A]">No subscription. No lock-in.</p>
            <h3 className="font-display text-2xl mt-2 font-light">A one-time engagement — not another 12-month contract.</h3>
            <ul className="mt-5 space-y-2.5 text-sm text-zinc-300">
              {["One-time Marketing Performance Assessment — no long-term agreement",
                "No monthly subscription and no implementation fee",
                "Priced by the size of the review, paid once",
                "Return for a quarterly or annual review only if you choose to"].map((t) => (
                <li key={t} className="flex gap-2.5"><Check className="h-4 w-4 text-[#D5A44A] shrink-0 mt-0.5" />{t}</li>
              ))}
            </ul>
          </div>
          <div className="border border-white/10 bg-[#12324D] p-7" data-testid="objection-independent">
            <p className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-[#0E7C7B]">Independent by design</p>
            <h3 className="font-display text-2xl mt-2 font-light">We're not your agency — and we take no vendor commissions.</h3>
            <ul className="mt-5 space-y-2.5 text-sm text-zinc-300">
              {["RetainProof is not a marketing agency and does not sell media",
                "We don't receive commissions for recommending marketing vendors",
                "Findings are tied to your own invoices, reports and retained-case data",
                "Independent of the vendors being measured"].map((t) => (
                <li key={t} className="flex gap-2.5"><ShieldCheck className="h-4 w-4 text-[#0E7C7B] shrink-0 mt-0.5" />{t}</li>
              ))}
            </ul>
            <Link to="/about" data-testid="objection-about-link" className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#D5A44A] hover:text-[#e6bd6f] transition-colors group">
              Why RetainProof exists <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="border border-white/10 bg-[#12324D] p-7" data-testid="objection-trust">
            <p className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-[#B54747]">Been burned before?</p>
            <h3 className="font-display text-2xl mt-2 font-light">Every finding is something your firm can verify.</h3>
            <ul className="mt-5 space-y-2.5 text-sm text-zinc-300">
              {["Built from your own invoices, vendor reports and retained-case records",
                "Documented line-by-line so leadership can independently confirm each finding",
                "Real reviews have surfaced $16,000+ and ~$8,000 in recoverable charges",
                "Confidential by design — we don't publish client names, and extend the same discretion to you"].map((t) => (
                <li key={t} className="flex gap-2.5"><ReceiptText className="h-4 w-4 text-[#B54747] shrink-0 mt-0.5" />{t}</li>
              ))}
            </ul>
            <a href="#client-outcomes" data-testid="objection-outcomes-link" className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#D5A44A] hover:text-[#e6bd6f] transition-colors group">
              See documented client outcomes <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </div>
      </section>

      {/* Legitimacy strip — honest signals only */}
      <section className="relative z-10 border-b border-white/10 bg-[#0B1F33]" data-testid="landing-trust-strip">
        <div className="max-w-7xl mx-auto px-6 py-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            [ShieldCheck, "Independent", "No vendor commissions. We don't sell media."],
            [Lock, "Your data stays yours", "Encryption in transit, per-firm isolation, export anytime."],
            [ReceiptText, "Evidence-based", "Findings tied to your invoices, reports and retained-case data."],
            [Check, "Secure checkout", "Assessment payments processed securely via Stripe."],
          ].map(([Icon, t, d]) => (
            <div key={t} className="flex gap-3" data-testid="trust-signal">
              <Icon className="h-5 w-5 text-[#D5A44A] shrink-0 mt-0.5" />
              <div>
                <p className="font-display text-base">{t}</p>
                <p className="text-xs text-zinc-400 mt-0.5 leading-relaxed">{d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Problem strip */}
      <section className="relative z-10 border-y border-white/10 bg-[#0B1F33]">
        <div className="max-w-4xl mx-auto px-6 py-12 flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-8 text-center">
          <span className="font-display text-2xl sm:text-3xl italic text-white/50">
            What your vendors <span className="not-italic text-white">report</span>
          </span>
          <span className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-[#D5A44A] border border-[#D5A44A]/40 rounded-full px-4 py-1.5 shrink-0">
            is not
          </span>
          <span className="font-display text-2xl sm:text-3xl italic text-white/50">
            What your firm <span className="not-italic text-white">retained</span>
          </span>
        </div>
      </section>

      {/* Independent journey / what you're paying for */}
      <section className="relative z-10 border-b border-white/10 bg-[#0A1B2E]" data-testid="landing-independent-journey">
        <div className="max-w-6xl mx-auto px-6 py-28">
          <p className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#D5A44A] mb-6">
            The independent journey
          </p>
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight leading-[1.05]">
                Agencies define success by <span className="italic text-[#D5A44A]">activity</span>. Your firm is defined by <span className="italic text-[#D5A44A]">retained matters</span>.
              </h2>
              <p className="mt-8 text-lg text-zinc-400 leading-relaxed">
                Clicks, impressions, website traffic, form submissions and connected calls do not reveal
                whether an inquiry was qualified, whether a consultation occurred, or whether the firm
                retained the matter.
              </p>
              <p className="mt-6 text-lg text-zinc-400 leading-relaxed">
                RetainProof<sup className="text-[0.5em] align-super">™</sup> gives firm leaders and marketing
                directors an independent way to evaluate the full journey — from agency promises and monthly
                charges to qualified conversations, referrals, retained matters and revenue.
              </p>
            </div>
            <div className="border border-white/10 bg-[#12324D] p-8" data-testid="paying-for-card">
              <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">
                Your firm may be paying for
              </p>
              <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm text-zinc-300">
                {[
                  "Multiple landing pages",
                  "Blogs & website content",
                  "Paid advertising campaigns",
                  "Legal directories",
                  "Lead-generation services",
                  "Call-tracking platforms",
                  "Search engine optimization",
                  "Social media management",
                ].map((it) => (
                  <li key={it} className="flex gap-2">
                    <Check className="h-4 w-4 text-[#0E7C7B] shrink-0 mt-0.5" />
                    {it}
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-sm text-zinc-400 leading-relaxed border-t border-white/10 pt-6">
                But which are contributing to retained matters? RetainProof compares what was
                <span className="text-white"> promised</span>, what was <span className="text-white">charged</span>,
                what activity was <span className="text-white">reported</span>, and what the firm ultimately
                <span className="text-white"> retained</span>.
              </p>
            </div>
          </div>

          <div className="mt-16 grid md:grid-cols-2 gap-8">
            <div className="border-l-2 border-[#0E7C7B] pl-6 py-1" data-testid="md-message">
              <p className="font-display text-2xl text-white">This is not your marketing director's fault.</p>
              <p className="mt-3 text-zinc-400 leading-relaxed">
                Marketing directors can only analyze the data agencies and lead providers make available.
                RetainProof gives them an independent system for validating that information against the
                firm's actual intake and retention results.
              </p>
            </div>
            <div className="border-l-2 border-[#D5A44A] pl-6 py-1" data-testid="call-classification-message">
              <p className="font-display text-2xl text-white">A connected call is not a qualified lead.</p>
              <p className="mt-3 text-zinc-400 leading-relaxed">
                RetainProof helps distinguish qualified conversations from spam, duplicate inquiries,
                irrelevant calls, existing-client calls, and prospects the firm could not serve.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-28">
        <p className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#D5A44A] mb-4">
          The independent source of truth
        </p>
        <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight mb-16 max-w-2xl">
          Every channel. Every vendor. One independent system — accountable to your firm, not your vendors.
        </h2>

        <div className="grid md:grid-cols-3 auto-rows-[minmax(220px,auto)] gap-8">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              custom={i}
              className={`group border border-white/10 bg-[#12324D] p-8 flex flex-col justify-between hover:border-[#D5A44A]/40 transition-colors duration-300 ${f.span}`}
              data-testid={`feature-card-${i}`}
            >
              <f.icon className="h-7 w-7 text-[#D5A44A]" strokeWidth={1.4} />
              <div>
                <h3 className="font-display text-2xl mb-2">{f.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{f.body}</p>
                {f.image && (
                  <img
                    src={f.image}
                    alt="AI invoice reconciliation flagging overcharges"
                    className="mt-6 w-full h-48 object-cover rounded-sm border border-white/10"
                    data-testid="invoice-recon-image"
                  />
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Founders / story */}
      <section className="relative z-10 border-t border-white/10 bg-[#0A1B2E]" data-testid="founders-section">
        <div className="max-w-5xl mx-auto px-6 py-28">
          <p className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#D5A44A] mb-6">
            Why we built this
          </p>
          <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight leading-[1.05] max-w-3xl">
            Built by a former law-firm marketing director.
          </h2>
          <div className="mt-10 grid md:grid-cols-2 gap-10 text-zinc-400 text-lg leading-relaxed">
            <p>
              RetainProof™ was created by a former law firm marketing director who spent years
              reviewing vendor reports full of calls, clicks, traffic and leads — while watching
              those numbers <span className="text-white">fail to match</span> the firm's actual caseload.
            </p>
            <p>
              A close review of vendor agreements, invoices and deliverables surfaced recurring
              charges for services the firm couldn't verify it was receiving. Challenging them — and
              restructuring the vendor relationships — protected real budget. That experience became
              <span className="text-white"> RetainProof™</span>.
            </p>
          </div>
          <blockquote className="mt-12 border-l-2 border-[#D5A44A] pl-6">
            <p className="font-display text-2xl sm:text-3xl font-light italic text-white leading-snug max-w-3xl">
              "We're allies to firms tired of paying inflated rates for bare-minimum
              service, only to receive vague, repetitive reports that never match the
              actual numbers."
            </p>
          </blockquote>
        </div>
      </section>

      {/* See it in action */}
      <section id="watch-demo" className="relative z-10 border-t border-white/10 bg-[#0B1F33]" data-testid="landing-video">
        <div className="max-w-5xl mx-auto px-6 py-24">
          <p className="font-mono-data text-[10px] uppercase tracking-widest text-[#0E7C7B] mb-4">
            See it in action
          </p>
          <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight max-w-3xl">
            Ninety seconds to understand RetainProof<sup className="text-[0.32em] align-super not-italic ml-[0.12em]">™</sup>.
          </h2>
          <div className="mt-10 border border-white/10 bg-black">
            <video
              controls
              preload="metadata"
              poster="https://d3uryq9bhgb5qr.cloudfront.net/FreeTrial/Google_102980368733255228124/0a83f95f-dd5d-44f2-ad3d-c2202b9eb106/image/0a83f95f-dd5d-44f2-ad3d-c2202b9eb106.jpg"
              className="w-full"
              data-testid="landing-video-player"
            >
              <source
                src="https://d3uryq9bhgb5qr.cloudfront.net/FreeTrial/Google_102980368733255228124/0a83f95f-dd5d-44f2-ad3d-c2202b9eb106/video/0a83f95f-dd5d-44f2-ad3d-c2202b9eb106/introducing_retainproof_the_ultimate_tool_for_trac.mp4"
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="relative z-10 border-t border-white/10 bg-[#0B1F33]" data-testid="landing-how-it-works">
        <div className="max-w-7xl mx-auto px-6 py-28">
          <p className="font-mono-data text-[10px] uppercase tracking-widest text-[#0E7C7B] mb-4">
            How it works
          </p>
          <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight max-w-3xl">
            From channel to cost per signed case — in three steps.
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mt-16">
            {[
              {
                n: "01",
                Icon: Megaphone,
                title: "Track every channel",
                body: "Connect ad platforms and log every marketing dollar by channel, campaign and office — independent of vendor dashboards.",
              },
              {
                n: "02",
                Icon: Building2,
                title: "Hold vendors accountable",
                body: "Reconcile invoices against contracts, catch overbilling with AI review, and score each vendor on real activity — not their reports.",
              },
              {
                n: "03",
                Icon: Scale,
                title: "See cost per signed case",
                body: "Attribute leads to signed cases and revenue, so you finally know which spend produces clients — and which just produces reports.",
              },
            ].map((step, i) => (
              <motion.div
                key={step.n}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fadeUp}
                className="relative border border-white/10 bg-[#12324D] p-8 flex flex-col"
                data-testid={`how-it-works-step-${i + 1}`}
              >
                <div className="flex items-center justify-between">
                  <step.Icon className="h-7 w-7 text-[#D5A44A]" />
                  <span className="font-mono-data text-3xl text-white/10">{step.n}</span>
                </div>
                <h3 className="font-display text-2xl mt-6">{step.title}</h3>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{step.body}</p>
                {i < 2 && (
                  <ArrowRight className="hidden md:block absolute top-8 -right-3 h-6 w-6 text-[#0E7C7B] z-10" />
                )}
              </motion.div>
            ))}
          </div>
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-16 flex flex-col sm:flex-row items-center gap-4"
            data-testid="how-it-works-cta"
          >
            <Button onClick={() => openConsult("baseline_review", "RetainProof Baseline Marketing Assessment", "Request a Baseline Marketing Assessment")} data-testid="how-it-works-cta-audit" className="bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-12 px-8 text-base font-medium group">
              Request a Baseline Marketing Assessment
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <button
              type="button"
              onClick={() => setDemoFirmOpen(true)}
              data-testid="how-it-works-cta-demo"
              className="inline-flex items-center gap-2 rounded-none h-12 px-8 text-base border border-[#0E7C7B] bg-[#0E7C7B]/15 text-[#4FD1CE] hover:bg-[#0E7C7B]/25 transition-colors"
            >
              <PlayCircle className="h-5 w-5" />
              Explore the live demo
            </button>
          </motion.div>
        </div>
      </section>

      <ClientOutcomes />

      {/* What you'll walk away with */}
      <section className="relative z-10 border-t border-white/10 bg-[#0A1B2E]" data-testid="landing-walkaway">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <p className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#D5A44A] mb-4">Your deliverables</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight max-w-3xl">What You'll Walk Away With</h2>
          <p className="mt-5 text-lg text-zinc-400 max-w-2xl leading-relaxed">A professional, independent assessment prepared for review by firm leadership — not a software login.</p>
          <div className="mt-12 grid lg:grid-cols-3 gap-6">
            {[
              { tag: "01 · Executive Findings", rows: [["Total marketing spend reviewed", "$84,000"], ["Vendors evaluated", "3"], ["Questioned charges", "$24,000+"], ["Reporting gaps", "4 identified"], ["Cost per retained matter", "$3,565 avg"]], note: "Key performance findings summarized for leadership." },
              { tag: "02 · Vendor Scorecards", rows: [["Vendor", "Agency A"], ["Spend", "$24,500"], ["Qualified conversations", "19"], ["Retained cases", "7"], ["Cost / retained case", "$3,500"], ["Verification status", "Needs documentation"]], note: "Every vendor scored on performance and verification." },
              { tag: "03 · Recommended Actions", list: ["Charges to challenge", "Contracts requiring clarification", "Vendors requiring documentation", "Spend to reconsider", "Spend worth preserving", "Reporting / process improvements"], note: "A clear action list — what to challenge, reduce, preserve or expand." },
            ].map((c) => (
              <div key={c.tag} className="border border-white/10 bg-[#12324D] flex flex-col" data-testid="walkaway-card">
                <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                  <p className="font-mono-data text-[10px] uppercase tracking-widest text-[#D5A44A]">{c.tag}</p>
                  <span className="font-mono-data text-[9px] uppercase tracking-widest text-zinc-600">Sample</span>
                </div>
                <div className="p-6 flex-1">
                  {c.rows && (
                    <div className="divide-y divide-white/10">
                      {c.rows.map((r) => (
                        <div key={r[0]} className="flex justify-between gap-3 py-2 text-sm"><span className="text-zinc-400">{r[0]}</span><span className="text-white font-mono-data text-right">{r[1]}</span></div>
                      ))}
                    </div>
                  )}
                  {c.list && (
                    <ul className="space-y-2.5">
                      {c.list.map((t) => (<li key={t} className="flex gap-2.5 text-sm text-zinc-200"><Check className="h-4 w-4 text-[#D5A44A] shrink-0 mt-0.5" />{t}</li>))}
                    </ul>
                  )}
                </div>
                <p className="px-6 pb-6 text-xs text-zinc-500 leading-relaxed">{c.note}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 font-mono-data text-[10px] uppercase tracking-widest text-zinc-600">Report contents shown are illustrative examples.</p>
        </div>
      </section>

      {/* Transformation: reports → answers */}
      <section className="relative z-10 border-t border-white/10 bg-[#0B1F33]" data-testid="landing-transformation">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight max-w-3xl">Go From Marketing Reports to Marketing Answers.</h2>
          <div className="mt-12 grid md:grid-cols-2 gap-6 items-start">
            <div className="border border-white/10 bg-[#0A1B2E] p-8" data-testid="transformation-before">
              <p className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-4">Before RetainProof</p>
              <p className="text-lg text-zinc-400 leading-relaxed">You know what your marketing vendors <span className="text-white">say</span> they delivered.</p>
              <p className="mt-4 text-sm text-zinc-500 leading-relaxed">The reporting comes from the same vendors being paid — activity, not outcomes, and rarely reconciled against what your firm actually retained.</p>
            </div>
            <div className="border border-[#D5A44A]/30 bg-[#12324D] p-8" data-testid="transformation-after">
              <p className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-[#D5A44A] mb-4">After RetainProof</p>
              <p className="text-sm text-zinc-300 mb-4">You know:</p>
              <ul className="space-y-2.5 text-sm text-zinc-200">
                {["What you paid", "What was contractually promised", "What was actually delivered", "What produced retained matters", "What could not be verified", "What deserves further review", "Where marketing dollars may need to be challenged or reallocated"].map((t) => (
                  <li key={t} className="flex gap-2.5"><Check className="h-4 w-4 text-[#D5A44A] shrink-0 mt-0.5" />{t}</li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-8 text-base text-zinc-400 max-w-3xl leading-relaxed">RetainProof gives your firm an independent view of marketing performance using your own contracts, invoices, vendor reports, lead data and retained-matter information.</p>
        </div>
      </section>

      {/* Pricing */}
      <section className="relative z-10 border-t border-white/10 bg-[#0B1F33]">
        <div className="max-w-7xl mx-auto px-6 py-28">
          <div className="mb-10 max-w-3xl">
            <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight">
              Know What Your Marketing Is Actually Producing.
            </h2>
            <p className="mt-5 text-lg text-zinc-300 leading-relaxed">
              RetainProof independently reconciles what your law firm spends, what your marketing vendors report, and the cases your firm actually retains.
            </p>
            <p className="mt-4 text-base text-zinc-400 leading-relaxed">
              Marketing agencies naturally report on the metrics they manage—impressions, clicks, calls, leads and campaign activity. RetainProof looks at the other side of the equation. We compare vendor activity and marketing spend with the firm's actual business outcomes to help leadership understand what is working, what is difficult to verify, where performance may be leaking, and what deserves a closer look.
            </p>
          </div>

          {/* How pricing works */}
          <div className="mb-14 border border-white/10 bg-[#12324D] p-8" data-testid="landing-how-pricing-works">
            <h3 className="font-display text-2xl font-light">Simple Pricing Based on the Size of the Review</h3>
            <p className="mt-2 text-sm text-zinc-400">RetainProof assessments are priced primarily according to:</p>
            <div className="mt-6 grid sm:grid-cols-3 gap-6">
              {[
                { icon: CalendarClock, t: "Lookback Period", d: "How much historical marketing activity we analyze." },
                { icon: Building2, t: "Number of Vendors & Channels", d: "How many agencies, lead generators, advertising platforms and marketing sources are included." },
                { icon: Gauge, t: "Complexity", d: "The volume of invoices, reports, lead data, retained-case information, markets, locations and attribution sources involved." },
              ].map((f) => (
                <div key={f.t}>
                  <f.icon className="h-5 w-5 text-[#D5A44A]" />
                  <p className="font-display text-lg mt-3">{f.t}</p>
                  <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{f.d}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 font-mono-data text-[11px] uppercase tracking-widest text-[#0E7C7B]">No long-term subscription is required to complete a RetainProof Marketing Performance Assessment.</p>
          </div>

          <div id="assessments" className="scroll-mt-24 grid md:grid-cols-2 xl:grid-cols-4 gap-6">
            {[
              { key: "standard_assessment", self: true, name: "Standard Assessment", price: "$1,500", lookback: "6-Month Lookback", vendors: "Up to 3 Vendors / Channels", best: "For a smaller or more concentrated marketing program.", support: "Know what your marketing is actually producing—not just what your vendors are reporting.", cta: "Start Your Assessment", items: ["6-month marketing performance review", "Review of up to 3 vendors and/or channels", "Marketing spend reconciliation", "Vendor invoice review", "Vendor performance report review", "Lead and inquiry analysis", "Retained-case analysis", "Cost-per-retained-case where data allows", "Unexplained charges, gaps & discrepancies", "Vendor accountability findings", "Executive findings report", "Strategic recommendations", "Executive review consultation"] },
              { key: "expanded_assessment", self: true, highlight: true, name: "Expanded Assessment", price: "$2,500", lookback: "12-Month Lookback", vendors: "Up to 5 Vendors / Channels", best: "A deeper view of where marketing dollars went and what they produced.", support: "A deeper view of where your marketing dollars went, what they produced, and where performance may be leaking.", cta: "Start Your Assessment", items: ["Everything in Standard, plus:", "Full 12-month historical review", "Analysis of up to 5 vendors / channels", "Year-over-year performance patterns", "Deeper vendor performance comparisons", "Marketing allocation analysis", "Lead-to-retained-case conversion analysis", "Long-term spending & performance trends", "Expanded findings & recommendations"] },
              { key: "comprehensive_assessment", name: "Comprehensive Assessment", pricePrefix: "Starting at", price: "$3,500", lookback: "12–18 Month Lookback", vendors: "Up to 8 Vendors / Channels", best: "For larger budgets, multiple agencies and complex ecosystems.", cta: "Request Comprehensive Assessment", items: ["12–18 months of marketing history", "Review of up to 8 vendors / channels", "Comprehensive spend reconciliation", "Vendor-by-vendor performance analysis", "Cross-channel comparison", "Lead-source attribution review", "Intake & retention performance where data allows", "Cost-per-lead & cost-per-retained-case analysis", "Duplicated / unsupported expense identification", "Marketing efficiency analysis", "Vendor accountability scorecard", "Budget allocation recommendations", "Comprehensive executive report", "Executive findings presentation / strategy session"] },
              { key: "enterprise_assessment", name: "Enterprise Assessment", price: "Custom", lookback: "Custom Lookback", vendors: "9+ Vendors / Channels", best: "For large or complex marketing operations.", cta: "Request Custom Assessment", items: ["Custom historical lookback", "9+ marketing vendors / channels", "Multi-office / multi-market analysis", "Large-scale spend reconciliation", "Complex attribution analysis", "Vendor & agency portfolio analysis", "Intake & retention analysis", "Executive-level findings", "Custom reporting", "Leadership presentation", "Strategic recommendations"] },
            ].map((tier) => (
              <div key={tier.key} className={`relative border p-7 flex flex-col ${tier.highlight ? "border-[#D5A44A] bg-[#14120A]" : "border-white/10 bg-[#12324D]"}`} data-testid={`assessment-${tier.key}`}>
                {tier.highlight && (
                  <span className="absolute -top-3 left-7 bg-[#D5A44A] text-black text-[10px] font-mono-data uppercase tracking-widest px-2 py-1">Most popular</span>
                )}
                <h3 className="font-display text-2xl tracking-tight">{tier.name}</h3>
                <p className="mt-3">
                  {tier.pricePrefix && <span className="block text-zinc-400 text-xs font-body">{tier.pricePrefix}</span>}
                  <span className="font-display text-4xl">{tier.price}</span>
                </p>
                <div className="mt-4 border-y border-white/10 py-3 space-y-1">
                  <p className="font-mono-data text-[11px] uppercase tracking-widest text-[#D5A44A]">{tier.lookback}</p>
                  <p className="font-mono-data text-[11px] uppercase tracking-widest text-[#0E7C7B]">{tier.vendors}</p>
                </div>
                <p className="mt-4 text-sm text-zinc-300 leading-relaxed">{tier.best}</p>
                <ul className="mt-5 space-y-2 text-sm text-zinc-400 flex-1">
                  {tier.items.map((it) => (
                    <li key={it} className="flex gap-2"><Check className="h-4 w-4 text-[#D5A44A] shrink-0 mt-0.5" />{it}</li>
                  ))}
                </ul>
                {tier.support && <p className="mt-5 text-xs text-zinc-500 italic leading-relaxed border-l-2 border-[#0E7C7B] pl-3">{tier.support}</p>}
                <Button
                  onClick={() => tier.self ? navigate(`/register?tier=${tier.key}`) : openConsult("private_consultation", tier.name, tier.cta)}
                  className={`mt-6 w-full rounded-none text-xs sm:text-sm h-11 ${tier.highlight ? "bg-[#D5A44A] text-black hover:bg-[#B8923D]" : "bg-white/10 text-white hover:bg-white/20"}`}
                  data-testid={`assessment-cta-${tier.key}`}
                >
                  {tier.cta}
                </Button>
                {tier.self && (
                  <div className="mt-3 space-y-2" data-testid={`assessment-assurance-${tier.key}`}>
                    <p className="text-[11px] text-zinc-500 leading-snug" data-testid="assessment-guarantee">
                      If your assessment doesn't surface at least one actionable finding, we'll work with you until it does.
                    </p>
                    <div className="flex items-center gap-1.5 text-[10px] font-mono-data uppercase tracking-wider text-zinc-500" data-testid="assessment-payment-marks">
                      <Lock className="h-3 w-3 text-[#0E7C7B]" /> Secure checkout via Stripe · Visa · Mastercard · Amex
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Onboarding + comparison + assessment + add-ons + FAQ */}
      <section className="relative z-10 border-t border-white/10 bg-[#0B1F33]">
        <div className="max-w-7xl mx-auto px-6 py-16" data-testid="landing-process">
          <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight mb-10">The assessment process</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { n: "1", t: "Submit", d: "The firm securely provides relevant marketing invoices, vendor reports, lead information and retained-case data." },
              { n: "2", t: "Reconcile", d: "RetainProof organizes and reconciles the firm's marketing spend, vendor-reported activity and actual outcomes." },
              { n: "3", t: "Analyze", d: "We identify performance gaps, discrepancies, attribution problems, inefficiencies and areas requiring additional scrutiny." },
              { n: "4", t: "Report", d: "Firm leadership receives an executive findings report with clear observations and recommendations." },
            ].map((s) => (
              <div key={s.n} className="border-t-2 border-[#D5A44A] bg-[#12324D] p-6" data-testid={`process-step-${s.n}`}>
                <p className="font-display text-3xl text-[#D5A44A]/60">{s.n}</p>
                <p className="font-display text-xl mt-2">{s.t}</p>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 pb-16" data-testid="landing-after-assessment">
          <div className="mb-8 max-w-3xl">
            <p className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-[#D5A44A]">After your assessment</p>
            <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight mt-2">Optional ongoing services</h2>
            <p className="mt-4 text-base text-zinc-400 leading-relaxed">The assessment gives your firm a clear benchmark. RetainProof can also help you measure what happens after changes are made.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: CalendarClock, name: "Quarterly Performance Review", price: "Starting at $1,000", unit: "per review", d: "For firms that want RetainProof to periodically compare current performance against their original assessment.", items: ["Updated marketing spend review", "Performance vs. previous assessment", "Vendor performance changes", "Retained-case performance", "Cost-per-retained-case changes", "Progress against recommendations", "Updated executive findings"], cta: "Ask About Quarterly Reviews", type: "quarterly_review" },
              { icon: Scale, name: "Annual Performance Review", price: "Starting at $2,000", unit: "per year", d: "An annual marketing accountability checkup.", items: ["Updated annual marketing analysis", "Performance vs. previous assessment", "Vendor performance review", "Spend & retained-case reconciliation", "Trend analysis", "Updated recommendations", "Executive report"], cta: "Ask About Annual Reviews", type: "annual_review" },
              { icon: ShieldCheck, name: "Executive Marketing Oversight", price: "Starting at $3,500", unit: "per month", badge: "Available by consultation", d: "A high-touch, custom advisory and marketing oversight engagement providing an independent layer between firm leadership and marketing vendors.", items: ["Ongoing vendor oversight", "Spend & invoice monitoring", "Vendor accountability", "Contract & deliverable monitoring", "Marketing & intake performance analysis", "Executive findings summaries", "Leadership strategy meetings", "Vendor meetings where appropriate", "Budget allocation & planning support"], cta: "Discuss Executive Oversight", type: "executive_oversight" },
            ].map((s) => (
              <div key={s.name} className="border border-white/10 bg-[#12324D] p-7 flex flex-col" data-testid={`ongoing-${s.type}`}>
                <s.icon className="h-6 w-6 text-[#D5A44A]" />
                <h3 className="font-display text-xl mt-4 leading-tight">{s.name}</h3>
                {s.badge && <span className="mt-2 inline-block w-fit border border-[#0E7C7B]/40 text-[#4FD1CE] text-[10px] font-mono-data uppercase tracking-widest px-2 py-0.5">{s.badge}</span>}
                <p className="mt-3"><span className="font-display text-2xl">{s.price}</span><span className="text-zinc-500 text-sm"> {s.unit}</span></p>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{s.d}</p>
                <ul className="mt-4 space-y-2 text-sm text-zinc-400 flex-1">
                  {s.items.map((it) => (<li key={it} className="flex gap-2"><Check className="h-4 w-4 text-[#0E7C7B] shrink-0 mt-0.5" />{it}</li>))}
                </ul>
                <Button onClick={() => openConsult("private_consultation", s.name, s.cta)} className="mt-6 w-full rounded-none bg-white/10 text-white hover:bg-white/20 h-11 text-xs sm:text-sm" data-testid={`ongoing-cta-${s.type}`}>{s.cta}</Button>
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 pb-20 grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display text-3xl font-light mb-4">Add-ons</h2>
            <details className="border border-white/10 bg-[#12324D]" data-testid="landing-additional-services">
              <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between font-display text-lg">
                Additional Services and Capacity
                <ChevronDown className="h-4 w-4 shrink-0 text-zinc-500" />
              </summary>
              <div className="px-5 pb-5">
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-zinc-400">
                  {["Additional vendors", "Additional locations", "Additional users", "Custom integrations", "Historical-data cleanup", "Contract digitization", "Custom reports", "Data migration", "Customized onboarding", "Additional executive consulting"].map((it) => (
                    <li key={it} className="flex gap-2"><ShieldCheck className="h-4 w-4 text-[#D5A44A] shrink-0 mt-0.5" />{it}</li>
                  ))}
                </ul>
                <p className="mt-4 font-mono-data text-[10px] uppercase tracking-widest text-[#D5A44A]">Quoted based on scope.</p>
              </div>
            </details>
            <p className="mt-6 font-mono-data text-[10px] text-zinc-500">Payments are administered by CMD Marketing Enterprises LLC.</p>
          </div>
          <div>
            <h2 className="font-display text-3xl font-light mb-4">Frequently asked</h2>
            <div className="border border-white/10 bg-[#12324D] divide-y divide-white/10" data-testid="landing-faq">
              {[
                ["How do I trust your numbers when I've been burned before?", "RetainProof works from your firm's own data and documentation — invoices, vendor reports, lead records and retained-matter information. Our analysis is independent of any marketing vendor, and every finding is tied to the evidence available so leadership can verify it. Independent reviews have surfaced documented, recoverable charges for the firms involved — see our client outcomes above."],
                ["Who else is using RetainProof?", "Our clients are personal-injury and plaintiff law firms, and we keep their identities confidential by design — we don't publish names or logos, and we'd extend the same discretion to your firm. What we can share are documented, anonymized outcomes: independent reviews have surfaced more than $16,000 and roughly $8,000 in recoverable charges for the firms involved."],
                ["Do I have to sign up for a subscription?", "No. A Marketing Performance Assessment is a one-time engagement. No long-term subscription is required. Some firms later choose an optional Quarterly or Annual review, or Executive Oversight — but that is entirely up to you."],
                ["Is my firm's data secure?", "Yes — encryption in transit, role-based access, hashed passwords, optional 2FA, per-firm isolation and audit logs."],
                ["Which systems do you work with?", "We work with the reports and data your firm and its vendors already produce — call-tracking such as CallRail, ad platforms such as Meta Ads and Google Ads, and case management such as Clio."],
                ["How is pricing determined?", "Pricing is based on the size of the review: the lookback period, the number of vendors and channels analyzed, and the overall complexity of your marketing program."],
                ["How does billing work?", "Assessments are administered by CMD Marketing Enterprises LLC and paid securely online for the Standard and Expanded tiers. Comprehensive, Enterprise and ongoing services are quoted and invoiced based on scope."],
                ["What do we receive?", "An independent executive findings report with clear observations and recommendations, plus an executive review consultation to walk through the results."],
              ].map(([q, a], i) => (
                <details key={q} data-testid={`landing-faq-${i}`}>
                  <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-3 text-sm text-zinc-200">
                    {q}
                    <ChevronDown className="h-4 w-4 shrink-0 text-zinc-500" />
                  </summary>
                  <p className="px-5 pb-4 text-sm text-zinc-400 leading-relaxed">{a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Closing statement */}
      <section className="relative z-10 border-t border-white/10 bg-[#0B1F33]" data-testid="landing-closing-statement">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <p className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-tight text-white/50">
            Your agencies report the <span className="italic">activity</span>.
            <br className="hidden sm:block" />
            <span className="text-white"> RetainProof<sup className="text-[0.3em] align-super not-italic ml-[0.1em]">™</sup> measures the outcome.</span>
          </p>
          <Button onClick={() => openConsult("general_consultation", "", "Schedule a RetainProof Consultation")} data-testid="closing-cta-register" className="mt-10 bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-12 px-8 text-base font-medium group">
            Request a consultation
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      </section>

      {/* Final message */}
      <section className="relative z-10 border-t border-white/10 bg-[#0B1F33]" data-testid="landing-final-message">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-tight">
            Stop relying solely on agency-reported analytics.
          </h2>
          <p className="mt-6 text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            Compare what third-party marketing agencies and lead generators promise with what your firm
            actually spends, the qualified conversations it receives, and the cases it ultimately retains.
          </p>
          <Button onClick={() => openConsult("general_consultation", "", "Schedule a RetainProof Consultation")} data-testid="final-cta" className="mt-10 bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-12 px-8 text-base font-medium group">
            Schedule a RetainProof Consultation
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row justify-between gap-4 text-sm text-zinc-500">
          <span className="flex items-center gap-2 font-body font-bold text-xl text-white"><img src="/retainproof-icon-dark.png" alt="RetainProof" className="h-6 w-auto" /><span>Retain<span className="text-[#0E7C7B]">Proof</span><sup className="text-[0.5em] font-normal align-super">™</sup></span></span>
          <div className="flex items-center gap-6">
            <Link to="/features" className="font-mono-data text-xs uppercase tracking-widest hover:text-white transition-colors" data-testid="footer-features-link">
              Features
            </Link>
            <Link to="/vs" className="font-mono-data text-xs uppercase tracking-widest hover:text-white transition-colors" data-testid="footer-vs-link">
              Why Independent
            </Link>
            <Link to="/insights" className="font-mono-data text-xs uppercase tracking-widest hover:text-white transition-colors" data-testid="footer-insights-link">
              Insights
            </Link>
            <Link to="/privacy" className="font-mono-data text-xs uppercase tracking-widest hover:text-white transition-colors" data-testid="footer-privacy-link">
              Privacy
            </Link>
            <Link to="/security" className="font-mono-data text-xs uppercase tracking-widest hover:text-white transition-colors" data-testid="footer-security-link">
              Security
            </Link>
            <Link to="/terms" className="font-mono-data text-xs uppercase tracking-widest hover:text-white transition-colors" data-testid="footer-terms-link">
              Terms
            </Link>
            <span className="font-mono-data text-xs">Independent marketing accountability for law firms · 2026</span>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 pb-8 -mt-4">
          <p className="font-mono-data text-xs text-zinc-600 max-w-3xl leading-relaxed" data-testid="footer-branding">
            RetainProof™ is a product of CMD Marketing Enterprises LLC through CMD+DesignLab. All contracts, subscriptions, and payments are administered by CMD Marketing Enterprises LLC.
          </p>
        </div>
      </footer>

      {/* Live demo email capture */}
      {demoFirmOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
          onClick={() => setDemoFirmOpen(false)}
          data-testid="demo-firm-modal"
        >
          <motion.form
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitDemoFirm}
            className="w-full max-w-md bg-[#0B1F33] border border-white/15 p-8"
          >
            <p className="font-mono-data text-[10px] uppercase tracking-widest text-[#0E7C7B] mb-3">Live demo · read-only</p>
            <h3 className="font-display text-3xl text-white">Step inside a live RetainProof<sup className="text-[0.32em] align-super not-italic ml-[0.12em]">™</sup> firm.</h3>
            <p className="text-sm text-zinc-400 mt-3">
              Enter your email and we'll drop you straight into a fully-loaded demo assessment workspace — no account, no password.
            </p>
            <Input
              type="email"
              required
              value={demoEmail}
              onChange={(e) => setDemoEmail(e.target.value)}
              placeholder="you@lawfirm.com"
              data-testid="demo-firm-email"
              className="mt-5 bg-white/5 border-white/15 rounded-none text-white h-12"
            />
            <Button
              type="submit"
              disabled={demoBusy}
              data-testid="demo-firm-submit"
              className="w-full mt-4 bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-12 font-medium"
            >
              {demoBusy ? "Loading demo…" : "Enter the live demo"}
            </Button>
            <p className="text-[10px] text-zinc-500 mt-3">By continuing you agree to receive occasional emails. Unsubscribe anytime.</p>
          </motion.form>
        </motion.div>
      )}

      {/* Demo lightbox */}
      {demoOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 sm:p-8"
          onClick={() => setDemoOpen(false)}
          data-testid="demo-lightbox"
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.25 }}
            className="relative w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setDemoOpen(false)}
              data-testid="demo-lightbox-close"
              className="absolute -top-11 right-0 inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors"
            >
              Close <X className="h-5 w-5" />
            </button>
            <video
              controls
              autoPlay
              preload="metadata"
              onPlay={() => { if (!demoTracked.current.play) { demoTracked.current.play = true; trackDemo("demo_play"); } }}
              onTimeUpdate={(e) => {
                const v = e.currentTarget;
                if (!v.duration) return;
                const pct = v.currentTime / v.duration;
                const t = demoTracked.current;
                if (pct >= 0.25 && !t.p25) { t.p25 = true; trackDemo("demo_p25"); }
                if (pct >= 0.5 && !t.p50) { t.p50 = true; trackDemo("demo_p50"); }
                if (pct >= 0.75 && !t.p75) { t.p75 = true; trackDemo("demo_p75"); }
              }}
              onEnded={() => { if (!demoTracked.current.done) { demoTracked.current.done = true; trackDemo("demo_complete"); } }}
              poster="https://d3uryq9bhgb5qr.cloudfront.net/FreeTrial/Google_102980368733255228124/0a83f95f-dd5d-44f2-ad3d-c2202b9eb106/image/0a83f95f-dd5d-44f2-ad3d-c2202b9eb106.jpg"
              className="w-full border border-white/15 bg-black shadow-2xl"
              data-testid="demo-lightbox-video"
            >
              <source
                src="https://d3uryq9bhgb5qr.cloudfront.net/FreeTrial/Google_102980368733255228124/0a83f95f-dd5d-44f2-ad3d-c2202b9eb106/video/0a83f95f-dd5d-44f2-ad3d-c2202b9eb106/introducing_retainproof_the_ultimate_tool_for_trac.mp4"
                type="video/mp4"
              />
            </video>
          </motion.div>
        </motion.div>
      )}

      <ConsultationForm
        open={consult.open}
        onClose={() => setConsult((c) => ({ ...c, open: false }))}
        requestType={consult.type}
        preferredPlan={consult.plan}
        title={consult.title}
      />
    </div>
  );
}
